import { useState, useCallback } from 'react';
import { INITIAL_FORM_DATA, CASE_TYPES, CHANNELS } from '../constants/formConstants';

/**
 * Custom hook for managing case creation form state
 * Handles form data, custom plan fields, and form validation
 */
const useCaseForm = (initialActionType = '') => {
  // Main form data state
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_DATA,
    atype: initialActionType,
  });

  // Custom fields for plans that have caseSpecificData
  const [customFieldValues, setCustomFieldValues] = useState({});

  /**
   * Handle form input changes
   * Resets custom field values when plan changes
   * @param {Event} e - Change event from form input
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset custom fields when plan changes
    if (name === 'plan') {
      setCustomFieldValues({});
    }
  }, []);

  /**
   * Handle custom plan field changes
   * @param {Event} e - Change event from custom field input
   */
  const handleCustomFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setCustomFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  /**
   * Update a single form field programmatically
   * @param {string} name - Field name
   * @param {any} value - Field value
   */
  const setField = useCallback((name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset custom fields when plan changes
    if (name === 'plan') {
      setCustomFieldValues({});
    }
  }, []);

  /**
   * Update multiple form fields at once
   * @param {object} fields - Object with field names and values
   */
  const setFields = useCallback((fields) => {
    setFormData((prev) => ({
      ...prev,
      ...fields,
    }));

    // Reset custom fields if plan is being changed
    if ('plan' in fields) {
      setCustomFieldValues({});
    }
  }, []);

  /**
   * Populate form from a saved case history record
   * @param {object} caseRecord - Saved case record from history
   * @param {object} caseRecord.formData - Saved form data
   * @param {object} caseRecord.customFieldValues - Saved custom field values
   */
  const setFormFromHistory = useCallback((caseRecord) => {
    if (!caseRecord) return;

    const { formData: savedFormData, customFieldValues: savedCustomFields } = caseRecord;

    if (savedFormData) {
      // Preserve the current action type when loading from history
      setFormData((prev) => ({
        ...savedFormData,
        atype: prev.atype,
      }));
    }

    if (savedCustomFields) {
      setCustomFieldValues(savedCustomFields);
    }
  }, []);

  /**
   * Reset form to initial state, optionally preserving certain fields
   * @param {object} preserveFields - Fields to preserve after reset
   */
  const resetForm = useCallback((preserveFields = {}) => {
    setFormData({
      ...INITIAL_FORM_DATA,
      ...preserveFields,
    });
    setCustomFieldValues({});
  }, []);

  /**
   * Reset form after successful case creation
   * Preserves atype, ctype, and channel
   */
  const resetAfterCreation = useCallback(() => {
    setFormData((prev) => ({
      ...INITIAL_FORM_DATA,
      atype: prev.atype,
      ctype: prev.ctype,
      channel: prev.channel,
      plan: '',
    }));
    setCustomFieldValues({});
  }, []);

  /**
   * Reset form after debug info fetch
   * Preserves atype
   */
  const resetAfterDebugFetch = useCallback(() => {
    setFormData((prev) => ({
      ...INITIAL_FORM_DATA,
      atype: prev.atype,
      caId: '',
    }));
  }, []);

  /**
   * Check if required fields for case creation are filled
   */
  const isCreateCaseValid = useCallback(() => {
    const { plan, phoneNumber, ctype, firstName, lastName, dob, gender } = formData;

    // Always required
    if (!plan || !phoneNumber) return false;

    // Additional fields required for Minimal/Custom case types
    const requiresPatientDetails =
      ctype === 'Custom - All fields' || ctype === 'Minimal - Select fields';

    if (requiresPatientDetails) {
      if (!firstName || !lastName || !dob || !gender) return false;
    }

    return true;
  }, [formData]);

  /**
   * Check if debug info form is valid
   */
  const isDebugInfoValid = useCallback(() => {
    return !!formData.caId;
  }, [formData.caId]);

  /**
   * Computed: Check if current case type requires patient details
   */
  const requiresPatientDetails =
    formData.ctype === 'Custom - All fields' || formData.ctype === 'Minimal - Select fields';

  /**
   * Computed: Check if current case type requires postal code
   */
  const requiresPostalCode = formData.ctype === 'Custom - All fields';

  return {
    // State
    formData,
    customFieldValues,

    // Event handlers
    handleChange,
    handleCustomFieldChange,

    // Programmatic updates
    setField,
    setFields,
    setFormData,
    setCustomFieldValues,
    setFormFromHistory,

    // Reset functions
    resetForm,
    resetAfterCreation,
    resetAfterDebugFetch,

    // Validation
    isCreateCaseValid,
    isDebugInfoValid,

    // Computed properties
    requiresPatientDetails,
    requiresPostalCode,

    // Constants for convenience
    caseTypes: CASE_TYPES,
    channels: CHANNELS,
  };
};

export default useCaseForm;
